import { Link } from "react-router";

import { FingerprintIcon, ShieldIcon } from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { ShowViewHeader } from "@/components/refine-ui/views/show-view";
import {
  DetailField,
  DetailGrid,
  DetailSection,
  RawJsonCard,
  ShowHero,
  SystemSection,
  useShowRecord,
} from "@/components/show";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ReadOnlyNote } from "@/pages/sports/components/read-only-note";
import { TeamSeasonStatsSection } from "@/pages/teams/components/team-season-stats-section";
import { TeamViewType, teamViewSchema } from "@/pages/teams/schemas/team.view";

/** `el: Τριφύλλι · en: The Greens`, or a dash. */
export const nicknameText = (nicknames?: Record<string, string> | null) => {
  const entries = Object.entries(nicknames ?? {}).filter(([, v]) => v);
  return entries.length
    ? entries.map(([locale, name]) => `${locale}: ${name}`).join(" · ")
    : "—";
};

export const TeamShow = () => {
  const { record, raw } = useShowRecord<TeamViewType>(
    teamViewSchema,
    (r) => `Team | ${r.name ?? r.id}`,
  );

  return (
    <div className="p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <ShowViewHeader />

        {record && (
          <>
            <ShowHero
              poster={record.media?.photos?.[0]}
              eyebrow={record.sport}
              title={record.name ?? "Untitled"}
              subtitle={[record.city, record.country]
                .filter(Boolean)
                .join(", ")}
              badges={
                <>
                  {record.status && (
                    <Badge variant="default">{record.status}</Badge>
                  )}
                  {record.hide && <Badge variant="secondary">Hidden</Badge>}
                </>
              }
            />

            <div className="flex justify-end">
              <Button asChild variant="outline">
                <Link to={`/matches?teamId=${record.id}`}>Matches</Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <DetailSection
                title="Details"
                icon={<ShieldIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="Sport" value={record.sport} />
                  <DetailField label="Country" value={record.country} />
                  <DetailField label="Gender" value={record.gender} />
                  <DetailField label="City" value={record.city} />
                  <DetailField label="Short name" value={record.shortName} />
                  <DetailField label="Founded" value={record.foundedYear} />
                  <DetailField label="Nicknames">
                    {nicknameText(record.nicknames)}
                  </DetailField>
                  <DetailField label="Home stadium">
                    {record.homeVenueId ? (
                      <Link
                        to={`/venues/edit/${record.homeVenueId}`}
                        className="font-mono hover:underline"
                      >
                        {record.homeVenueId}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </DetailField>
                  <DetailField label="Status" value={record.status} />
                  <DetailField
                    label="Hidden"
                    value={
                      record.hide == null
                        ? undefined
                        : record.hide
                          ? "Yes"
                          : "No"
                    }
                  />
                </DetailGrid>
              </DetailSection>

              <DetailSection
                title="Source"
                icon={<FingerprintIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="ID" mono>
                    <span className="inline-flex items-center gap-1">
                      {record.id}
                      <CopyButton text={record.id} message="Copied team ID" />
                    </span>
                  </DetailField>
                  <DetailField
                    label="Highlightly ID"
                    value={record.metadata?.sourceId}
                    mono
                  />
                  <DetailField label="Wikidata" mono>
                    {record.wikidataId ? (
                      <a
                        href={`https://www.wikidata.org/wiki/${record.wikidataId}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline"
                      >
                        {record.wikidataId}
                      </a>
                    ) : (
                      "—"
                    )}
                  </DetailField>
                </DetailGrid>
                <ReadOnlyNote />
              </DetailSection>
            </div>

            <TeamSeasonStatsSection teamId={record.id} />

            <SystemSection record={record} />

            <RawJsonCard data={raw} />
          </>
        )}
      </div>
    </div>
  );
};
